/* eslint-disable no-unused-vars */
import React from "react";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
  Tooltip,
} from "recharts";
import Expand from "../../../../../assets/images/expand.svg";

/* ✅ K / L / Cr FORMATTER */
const formatToCappedUnit = (value) => {
  if (value === null || value === undefined) return "-";

  if (value < 1000) return value.toLocaleString("en-IN");
  if (value < 100000) return `${(value / 1000).toFixed(1)} K`;
  if (value < 10000000) return `${(value / 100000).toFixed(1)} L`;

  return `${(value / 10000000).toFixed(1)} Cr`;
};


const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const data = payload[0].payload;

    return (
      <div
        style={{
          background: "#fff",
          padding: "10px 10px",
          borderRadius: "8px",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.15)",
          border: "1px solid #eee",
          fontSize: "13px",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "6px" }}>
          {data.name}
        </div>

        <div style={{ color: "#5A8DEE" }}>
          Loss Quantity &nbsp;&nbsp; {data.value.toLocaleString("en-IN")} {data.suffix}
        </div>

        <div style={{ color: "#9CA3AF" }}>
          Percentage &nbsp;&nbsp; {Number(data.percent).toFixed(1)}%
        </div>
      </div>
    );
  }

  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        suffix: PropTypes.string,
        percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    })
  ),
};

const formatYAxisLabel = (value) => {
  if (!value) return "";
  const words = value.split(" ");

  const lines = [];
  let current = "";

  words.forEach((word) => {
    if ((current + " " + word).length > 25) {
      lines.push(current);
      current = word;
    } else {
      current = current ? current + " " + word : word;
    }
  });

  if (current) lines.push(current);
  return lines.join("\n");
};

const CustomYAxisTick = ({ x, y, payload }) => {
  const value = payload.value;

  // Split into multiple lines (same logic you used)
  const words = value.split(" ");
  const lines = [];
  let current = "";

  words.forEach((word) => {
    if ((current + " " + word).length > 25) {
      lines.push(current);
      current = word;
    } else {
      current = current ? current + " " + word : word;
    }
  });

  if (current) lines.push(current);

  return (
    <text x={x} y={y} textAnchor="end" fontSize={12} fill="#333">
      {lines.map((line, index) => (
        <tspan
          key={line}
          x={x}
          dy={index === 0 ? 0 : 12}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
};

CustomYAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.string,
  }),
};

const LossBarLabel = ({ data, x, y, width, height, value, index }) => {
  const item = data?.[index];
  if (!item) return null;

  return (
    <text
      x={x + width + 10}
      y={y + height / 2}
      fill="#333"
      fontSize={12}
      dominantBaseline="middle"
    >
      {`${formatToCappedUnit(value)} ${item?.suffix} (${Number(item?.percent).toFixed(1)}%)`}
    </text>
  );
};

LossBarLabel.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      suffix: PropTypes.string,
      percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  index: PropTypes.number,
};

const AllocationToDeliveryLossReasonsGraph = ({
  data,
  title,
  handleExpand,
  showExpanded,
}) => {
  const dynamicHeight = Math.max(data.length * 55, 160);

  const yAxisWidth = 200;
  const rightMargin = 200;
  const maxValue = Math.max(...data.map((item) => Number(item.value) || 0), 0);

  // Optional fixed ticks for better readability/alignment
  const ticks =
    maxValue > 0
      ? [0, Math.round(maxValue / 4), Math.round(maxValue / 2), Math.round((maxValue * 3) / 4), maxValue]
      : [0];

  return (
    <div style={styles.container}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={styles.title}>{title}</h3>

        {!showExpanded && <div className={styles.segmentedBtn}>
          <button
            type="button"
            className={styles.segment}
            style={{ padding: "3px", background: "white", border: "1px solid #e5e7eb", cursor: "pointer", marginBottom: "4px", position: "relative", bottom: "5px" }}
            onClick={handleExpand}
            title="Expand / Zoom"
          >
            <img src={Expand} alt="Expand" className={styles.icon} />
          </button>
        </div>}
      </div>

      <div style={styles.scrollWrapper}>
        <div style={styles.chartInner}>
          {/* Main Chart */}
          <ResponsiveContainer width="100%" height={dynamicHeight}>
            <BarChart
              layout="vertical"
              data={data}
              margin={{ top: 10, right: rightMargin, left: 0, bottom: 10 }}
              barCategoryGap="20%"
            >
              <CartesianGrid stroke="#E5E7EB" horizontal={false} vertical={true} />

              {/* Hidden X-axis for proper scaling */}
              <XAxis
                type="number"
                domain={[0, maxValue]}
                hide
              />

              <YAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tickFormatter={formatYAxisLabel}
                width={yAxisWidth}
                interval={0}
                tick={<CustomYAxisTick />}
              />

              <Tooltip content={<CustomTooltip />} />

              <Bar
                dataKey="value"
                fill="#9EDAE5"
                radius={[0, 10, 10, 0]}
                barSize={14}
              >
                <LabelList
                  data={data}
                  content={<LossBarLabel data={data} />}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Sticky bottom X-axis */}
          <div style={styles.stickyBottomAxis}>
            <ResponsiveContainer width="100%" height={35}>
              <BarChart
                layout="vertical"
                data={[{ value: maxValue }]}
                margin={{
                  top: 0,
                  right: rightMargin,
                  left: yAxisWidth,
                  bottom: 0,
                }}
              >
                <XAxis
                  type="number"
                  domain={[0, maxValue]}
                  ticks={ticks}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

AllocationToDeliveryLossReasonsGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      suffix: PropTypes.string,
    })
  ),
  title: PropTypes.string,
  handleExpand: PropTypes.func,
  showExpanded: PropTypes.bool,
};

const styles = {
  container: {
    background: "#fff",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    width: "100%",
    overflow: "hidden",
  },

  title: {
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "12px",
  },

  chartWrapper: {
    height: "300px",
    overflowY: "auto",
  },

  scrollWrapper: {
    width: "100%",
    height: "250px",
    overflowX: "auto",
    overflowY: "auto",
  },

  chartInner: {
    width: "800px",
    position: "relative",
  },

  stickyBottomAxis: {
    position: "sticky",
    bottom: 0,
    background: "#fff",
    zIndex: 5,
  },
};

export default AllocationToDeliveryLossReasonsGraph;